import type { Prayer } from '@/types/prayer';
import { normalizeForSearch } from './search-normalize';
import {
  commonPrefixLength,
  osaDistanceWithin,
} from './edit-distance';

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'it', 'that', 'this', 'was', 'are',
  'be', 'has', 'had', 'have', 'do', 'does', 'did', 'will', 'would',
  'could', 'should', 'may', 'might', 'shall', 'can', 'not', 'no', 'so',
  'if', 'then', 'than', 'too', 'very', 'just', 'about', 'up', 'out',
  'my', 'me', 'we', 'our', 'you', 'your', 'he', 'she', 'his', 'her',
  'they', 'them', 'their', 'its', 'i', 'am', 'been', 'being', 'all',
  'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some',
  'such', 'only', 'own', 'same', 'over', 'also', 'after', 'before',
  'pray', 'prayer', 'praying', 'prayed', 'god', 'lord', 'jesus', 'please',
]);

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w));
}

function overlapScore(queryTokens: string[], targetTokens: string[]): number {
  const targetSet = new Set(targetTokens);
  let hits = 0;
  for (const w of queryTokens) {
    if (targetSet.has(w)) hits++;
  }
  return hits;
}

// --- Indexed Garden matcher ------------------------------------------------
//
// Every surface the user might remember typing remains searchable. Private
// prayers are intentionally included; outbound sharing owns the privacy gate.
// Query work uses a local token index so a rare term evaluates only its
// candidates. Broad queries still do work proportional to the true match set,
// which is necessary for an exact count, but never proportional to every field
// byte on every keystroke.

export const PRAYER_SEARCH_MAX_QUERY_LENGTH = 256;
export const PRAYER_SEARCH_MAX_FUZZY_TOKENS = 12;
export const PRAYER_SEARCH_MAX_FUZZY_TOKEN_LENGTH = 48;
export const PRAYER_SEARCH_MAX_TIER_CHARACTERS = 32_768;
export const PRAYER_SEARCH_MAX_INDEX_TOKEN_LENGTH = 256;
export const PRAYER_SEARCH_MAX_RELATED_TERMS = 48;

const FUZZY_MIN_LEN = 4;
const FUZZY_LONG_LEN = 7;
const MAX_FUZZY_EDIT_CALLS = 2048;
const MAX_FUZZY_TOKEN_VISITS = 20_000;
const TIER_WEIGHTS = [50, 40, 30, 20, 10] as const;
const SEARCH_TOKEN_RE = /[\p{L}\p{N}]+|[\p{S}]+/gu;

type MatchQuality = 'exact' | 'prefix' | 'substr' | 'none';

interface Tier {
  text: string;
  sorted: string[];
}

interface SearchSourceSnapshot {
  who: string | undefined;
  mentionedPeople: string[];
  gardenSummary: string | undefined;
  corePrayer: string | undefined;
  rawText: string;
  progressNotes: string[];
  testimony: string | null | undefined;
  scriptureReference: string | undefined;
  scriptureText: string | undefined;
}

interface NormalizedFields {
  tiers: Tier[];
  source: SearchSourceSnapshot;
}

export interface PrayerSearchMetrics {
  normalizationCount: number;
  cacheHitCount: number;
  cacheInvalidationCount: number;
  indexedPrayerCount: number;
  indexedTokenCount: number;
  postingCount: number;
  trigramLinks: number;
  bigramLinks: number;
  prefixLinks: number;
  normalizedFieldBytes: number;
  retainedBytesEstimate: number;
  queryTokenCount: number;
  tokenComparisons: number;
  postingVisits: number;
  prayerEvaluations: number;
  candidateCount: number;
  fuzzyTokenVisits: number;
  editDistanceCalls: number;
  truncatedTierCount: number;
  skippedOverlongTokenCount: number;
}

export interface PrayerSearchIndex {
  readonly scopeKey: string;
  readonly prayers: readonly Prayer[];
  readonly fields: readonly NormalizedFields[];
  readonly tokenList: readonly string[];
  readonly tokenIdByToken: ReadonlyMap<string, number>;
  /** Encoded `(prayerIndex * 32) + tierMask` values. */
  readonly postings: readonly (readonly number[])[];
  readonly shortPrefixTokenIds: ReadonlyMap<string, readonly number[]>;
  readonly trigramTokenIds: ReadonlyMap<string, readonly number[]>;
  readonly bigramTokenIds: ReadonlyMap<string, readonly number[]>;
  readonly charTokenIds: ReadonlyMap<string, readonly number[]>;
}

export interface PrayerSearchResult {
  prayers: Prayer[];
  fuzzy: boolean;
  related: boolean;
  /**
   * Why each prayer was included. Aggregate `related` / `fuzzy` flags remain
   * for existing callers; mixed-result UIs should use this map so only the
   * cards reached through curated wording receive related-result treatment.
   */
  matchKindByPrayerId: ReadonlyMap<string, PrayerSearchMatchKind>;
  normalizedQuery: string;
  queryTokens: string[];
}

export type PrayerSearchMatchKind = 'direct' | 'related' | 'fuzzy';

export type PrayerSearchContextKind = 'visible' | 'authored' | 'related' | 'details';

export function createPrayerSearchMetrics(): PrayerSearchMetrics {
  return {
    normalizationCount: 0,
    cacheHitCount: 0,
    cacheInvalidationCount: 0,
    indexedPrayerCount: 0,
    indexedTokenCount: 0,
    postingCount: 0,
    trigramLinks: 0,
    bigramLinks: 0,
    prefixLinks: 0,
    normalizedFieldBytes: 0,
    retainedBytesEstimate: 0,
    queryTokenCount: 0,
    tokenComparisons: 0,
    postingVisits: 0,
    prayerEvaluations: 0,
    candidateCount: 0,
    fuzzyTokenVisits: 0,
    editDistanceCalls: 0,
    truncatedTierCount: 0,
    skippedOverlongTokenCount: 0,
  };
}

export function boundPrayerSearchInput(value: string): string {
  return value.slice(0, PRAYER_SEARCH_MAX_QUERY_LENGTH);
}

function searchTokens(normalized: string): string[] {
  return normalized.match(SEARCH_TOKEN_RE) ?? [];
}

function preparedQuery(query: string): {
  normalized: string;
  words: string[];
} {
  const normalized = normalizeForSearch(boundPrayerSearchInput(query));
  return {
    normalized,
    // Repeating a word expresses the same AND predicate. Deduplicating keeps it
    // from multiplying one term's score and changing otherwise equal intent.
    words: [...new Set(searchTokens(normalized))],
  };
}

function buildTier(
  parts: Array<string | undefined | null>,
  metrics?: PrayerSearchMetrics,
): Tier {
  let source = '';
  let remaining = PRAYER_SEARCH_MAX_TIER_CHARACTERS;
  let truncated = false;
  for (const part of parts) {
    if (!part) continue;
    if (remaining <= 0) {
      truncated = true;
      continue;
    }
    if (source) {
      source += ' ';
      remaining -= 1;
    }
    if (part.length > remaining) {
      source += part.slice(0, Math.max(0, remaining));
      remaining = 0;
      truncated = true;
    } else {
      source += part;
      remaining -= part.length;
    }
  }
  const text = normalizeForSearch(source).slice(0, PRAYER_SEARCH_MAX_TIER_CHARACTERS);
  const tokens = searchTokens(text);
  const boundedTokens = tokens.filter(
    (token) => token.length <= PRAYER_SEARCH_MAX_INDEX_TOKEN_LENGTH,
  );
  if (metrics) {
    if (truncated) metrics.truncatedTierCount += 1;
    metrics.skippedOverlongTokenCount += tokens.length - boundedTokens.length;
  }
  return { text, sorted: [...new Set(boundedTokens)].sort() };
}

// Production store/lifecycle writes replace Prayer objects, which is still the
// required React notification contract. The source snapshot is a second line
// of defense: if a future rebuild receives an object that was mutated in place,
// including a nested mentioned-person or progress-note edit, stale normalized
// text is rejected instead of reused. WeakMap ownership means old account
// objects are not retained.
const fieldsCache = new WeakMap<Prayer, NormalizedFields>();

function sameStrings(current: readonly string[], cached: readonly string[]): boolean {
  if (current.length !== cached.length) return false;
  for (let index = 0; index < current.length; index += 1) {
    if (current[index] !== cached[index]) return false;
  }
  return true;
}

function sourceStillMatches(prayer: Prayer, source: SearchSourceSnapshot): boolean {
  const mentionedPeople = prayer.parsed?.mentionedPeople ?? [];
  const progressHistory = prayer.progressHistory ?? [];
  return (
    prayer.parsed?.who === source.who &&
    sameStrings(mentionedPeople, source.mentionedPeople) &&
    prayer.gardenSummary === source.gardenSummary &&
    prayer.parsed?.core_prayer === source.corePrayer &&
    prayer.rawText === source.rawText &&
    progressHistory.length === source.progressNotes.length &&
    progressHistory.every((entry, index) => entry.note === source.progressNotes[index]) &&
    prayer.testimony === source.testimony &&
    prayer.scriptureAnchor?.reference === source.scriptureReference &&
    prayer.scriptureAnchor?.text === source.scriptureText
  );
}

function prayerFields(
  prayer: Prayer,
  metrics?: PrayerSearchMetrics,
): NormalizedFields {
  const cached = fieldsCache.get(prayer);
  if (cached && sourceStillMatches(prayer, cached.source)) {
    if (metrics) metrics.cacheHitCount += 1;
    return cached;
  }
  if (cached && metrics) metrics.cacheInvalidationCount += 1;
  if (metrics) metrics.normalizationCount += 1;
  const source: SearchSourceSnapshot = {
    who: prayer.parsed?.who,
    mentionedPeople: [...(prayer.parsed?.mentionedPeople ?? [])],
    gardenSummary: prayer.gardenSummary,
    corePrayer: prayer.parsed?.core_prayer,
    rawText: prayer.rawText,
    progressNotes: (prayer.progressHistory ?? []).map((entry) => entry.note),
    testimony: prayer.testimony,
    scriptureReference: prayer.scriptureAnchor?.reference,
    scriptureText: prayer.scriptureAnchor?.text,
  };
  const tiers = [
    buildTier([source.who, ...source.mentionedPeople], metrics),
    buildTier([source.gardenSummary, source.corePrayer], metrics),
    buildTier([source.rawText], metrics),
    buildTier(source.progressNotes, metrics),
    buildTier([
      source.testimony,
      source.scriptureReference,
      source.scriptureText,
    ], metrics),
  ];
  const fields = { tiers, source };
  fieldsCache.set(prayer, fields);
  return fields;
}

const viewIndexCache = new WeakMap<Prayer[], {
  scopeKey: string;
  fields: readonly (NormalizedFields | undefined)[];
  index: PrayerSearchIndex;
}>();

/**
 * The Garden's index for one exact corpus array and owner scope, as resumable
 * steps (P2, 2026-10-16): normalization, token, posting and n-gram work yields
 * after each prayer or token so `runStepsInSlices` can spread it over idle
 * slices. The finished index is kept per array + scope, weakly keyed: a
 * replaced corpus or another account's array never reuses it. A kept index is
 * reused only while every prayer still has the exact cached fields it was
 * built from and their sources still match, so a prayer changed in place
 * forces a rebuild, as a direct rebuild would (rule #38).
 */
export function* prayerSearchIndexSteps(
  prayers: Prayer[],
  scopeKey: string,
): Generator<void, PrayerSearchIndex, void> {
  const cached = viewIndexCache.get(prayers);
  if (cached && cached.scopeKey === scopeKey) {
    let current = true;
    for (let index = 0; index < prayers.length && current; index += 1) {
      const fields = fieldsCache.get(prayers[index]);
      current = fields !== undefined && fields === cached.fields[index] &&
        sourceStillMatches(prayers[index], fields.source);
      yield;
    }
    if (current) return cached.index;
  }
  const index = yield* buildPrayerSearchIndexSteps(prayers, scopeKey);
  viewIndexCache.set(prayers, { scopeKey, fields: index.fields, index });
  return index;
}

/** Run resumable steps to completion in one call. */
export function runStepsNow<R>(steps: Generator<void, R, void>): R {
  for (;;) {
    const next = steps.next();
    if (next.done) return next.value;
  }
}

/**
 * Run resumable steps in bounded idle slices (see runInIdleSlices); `onDone`
 * receives the result. The returned function cancels obsolete work, closes
 * the steps, and guarantees `onDone` never runs afterwards.
 */
export function runStepsInSlices<R>(
  steps: Generator<void, R, void>,
  onDone: (result: R) => void,
): () => void {
  let result: { value: R } | null = null;
  const cancel = runInIdleSlices((deadline) => {
    do {
      const next = steps.next();
      if (next.done) {
        result = { value: next.value };
        return false;
      }
    } while (deadline.timeRemaining() > 0);
    return true;
  }, () => {
    if (result) onDone(result.value);
  });
  return () => {
    cancel();
    steps.return(undefined as R);
  };
}

/** Time-budget view handed to each background slice. */
export interface SliceDeadline {
  timeRemaining(): number;
}

/**
 * Run bounded background work in small slices (P2, 2026-10-16). Each slice
 * gets at most `sliceMs` (less when the browser reports less idle time) and
 * `step` returns true while work remains. Uses requestIdleCallback where it
 * exists, with a timeout so the work cannot starve, and a zero-delay timer
 * where it does not (Safari). The returned function cancels obsolete work;
 * `done` never runs after cancellation.
 */
export function runInIdleSlices(
  step: (deadline: SliceDeadline) => boolean,
  done: () => void,
  sliceMs = 8,
): () => void {
  type IdleCallbacks = {
    requestIdleCallback?: (callback: (deadline: { timeRemaining(): number }) => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  const host = (typeof window === 'undefined' ? {} : window) as IdleCallbacks;
  const idle = typeof host.requestIdleCallback === 'function' && typeof host.cancelIdleCallback === 'function'
    ? host
    : null;
  let cancelled = false;
  let idleHandle: number | null = null;
  let timerHandle: ReturnType<typeof setTimeout> | null = null;
  const run = (idleDeadline?: { timeRemaining(): number }) => {
    idleHandle = null;
    timerHandle = null;
    if (cancelled) return;
    const started = performance.now();
    const budget = Math.min(sliceMs, idleDeadline ? Math.max(idleDeadline.timeRemaining(), 1) : sliceMs);
    const more = step({ timeRemaining: () => Math.max(0, budget - (performance.now() - started)) });
    if (cancelled) return;
    if (more) schedule();
    else done();
  };
  const schedule = () => {
    if (idle) idleHandle = idle.requestIdleCallback!(run, { timeout: 250 });
    else timerHandle = setTimeout(run, 0);
  };
  schedule();
  return () => {
    cancelled = true;
    if (idleHandle !== null) idle?.cancelIdleCallback!(idleHandle);
    if (timerHandle !== null) clearTimeout(timerHandle);
  };
}

function pushMapArray<K>(map: Map<K, number[]>, key: K, value: number): void {
  const current = map.get(key);
  if (current) current.push(value);
  else map.set(key, [value]);
}

function uniqueNgrams(value: string, size: number): string[] {
  if (value.length < size) return [];
  const grams = new Set<string>();
  for (let index = 0; index <= value.length - size; index += 1) {
    grams.add(value.slice(index, index + size));
  }
  return [...grams];
}

/** Build one owner/generation-scoped, local-only search index. */
export function buildPrayerSearchIndex(
  prayers: Prayer[],
  scopeKey = 'local',
  metrics?: PrayerSearchMetrics,
): PrayerSearchIndex {
  return runStepsNow(buildPrayerSearchIndexSteps(prayers, scopeKey, metrics));
}

// The one build implementation; it yields after each prayer or token so the
// same work can run whole (buildPrayerSearchIndex) or in idle slices.
function* buildPrayerSearchIndexSteps(
  prayers: Prayer[],
  scopeKey: string,
  metrics?: PrayerSearchMetrics,
): Generator<void, PrayerSearchIndex, void> {
  const fields: NormalizedFields[] = [];
  for (const prayer of prayers) {
    fields.push(prayerFields(prayer, metrics));
    yield;
  }
  // Stable lexical ids make bounded fuzzy candidate selection independent of
  // sync arrival order. The second pass below still keeps postings ordered by
  // corpus index; final prayer ordering has its own stable id tiebreak.
  const tokenUniverse = new Set<string>();
  for (const normalized of fields) {
    for (const tier of normalized.tiers) {
      for (const token of tier.sorted) tokenUniverse.add(token);
    }
    yield;
  }
  const tokenList = [...tokenUniverse].sort();
  const tokenIdByToken = new Map(
    tokenList.map((token, tokenId) => [token, tokenId] as const),
  );
  const postings: number[][] = Array.from({ length: tokenList.length }, () => []);

  for (let prayerIndex = 0; prayerIndex < prayers.length; prayerIndex += 1) {
    const tokenMasks = new Map<string, number>();
    for (let tierIndex = 0; tierIndex < fields[prayerIndex].tiers.length; tierIndex += 1) {
      for (const token of fields[prayerIndex].tiers[tierIndex].sorted) {
        tokenMasks.set(token, (tokenMasks.get(token) ?? 0) | (1 << tierIndex));
      }
    }
    for (const [token, tierMask] of tokenMasks) {
      const tokenId = tokenIdByToken.get(token)!;
      postings[tokenId].push(prayerIndex * 32 + tierMask);
    }
    yield;
  }

  const shortPrefixTokenIds = new Map<string, number[]>();
  const trigramTokenIds = new Map<string, number[]>();
  const bigramTokenIds = new Map<string, number[]>();
  const charTokenIds = new Map<string, number[]>();
  let trigramLinks = 0;
  let bigramLinks = 0;
  let prefixLinks = 0;
  let charLinks = 0;
  for (let tokenId = 0; tokenId < tokenList.length; tokenId += 1) {
    const token = tokenList[tokenId];
    for (let length = 1; length <= Math.min(2, token.length); length += 1) {
      pushMapArray(shortPrefixTokenIds, token.slice(0, length), tokenId);
      prefixLinks += 1;
    }
    for (const gram of uniqueNgrams(token, 3)) {
      pushMapArray(trigramTokenIds, gram, tokenId);
      trigramLinks += 1;
    }
    for (const gram of uniqueNgrams(token, 2)) {
      pushMapArray(bigramTokenIds, gram, tokenId);
      bigramLinks += 1;
    }
    for (const character of new Set(Array.from(token))) {
      pushMapArray(charTokenIds, character, tokenId);
      charLinks += 1;
    }
    yield;
  }

  const postingCount = postings.reduce((total, list) => total + list.length, 0);
  const tokenBytes = tokenList.reduce((total, token) => total + token.length * 2, 0);
  const normalizedFieldBytes = fields.reduce(
    (total, normalized) =>
      total +
      normalized.tiers.reduce(
        (tierTotal, tier) =>
          tierTotal + tier.text.length * 2 + tier.sorted.length * 8,
        0,
      ) +
      (normalized.source.mentionedPeople.length + normalized.source.progressNotes.length) * 8 +
      80,
    0,
  );
  const retainedBytesEstimate =
    normalizedFieldBytes +
    tokenBytes +
    postingCount * 4 +
    (trigramLinks + bigramLinks + prefixLinks + charLinks) * 4 +
    prayers.length * 144;
  if (metrics) {
    metrics.indexedPrayerCount = prayers.length;
    metrics.indexedTokenCount = tokenList.length;
    metrics.postingCount = postingCount;
    metrics.trigramLinks = trigramLinks;
    metrics.bigramLinks = bigramLinks;
    metrics.prefixLinks = prefixLinks;
    metrics.normalizedFieldBytes = normalizedFieldBytes;
    metrics.retainedBytesEstimate = retainedBytesEstimate;
  }

  return {
    scopeKey,
    prayers: [...prayers],
    fields,
    tokenList,
    tokenIdByToken,
    postings,
    shortPrefixTokenIds,
    trigramTokenIds,
    bigramTokenIds,
    charTokenIds,
  };
}

function tokenAtOrAfter(sorted: string[], word: string): string | undefined {
  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (sorted[middle] < word) low = middle + 1;
    else high = middle;
  }
  return sorted[low];
}

function qualityInTier(word: string, tier: Tier): MatchQuality {
  const candidate = tokenAtOrAfter(tier.sorted, word);
  if (candidate === word) return 'exact';
  if (candidate?.startsWith(word)) return 'prefix';
  if (tier.text.includes(word)) return 'substr';
  return 'none';
}

function qualityBonus(length: number, quality: MatchQuality): number {
  if (quality === 'exact') return length <= 3 ? 100 : 5;
  if (quality === 'prefix') return length <= 3 ? 50 : 2;
  return 0;
}

function scoreWord(word: string, fields: NormalizedFields): number | null {
  let best = 0;
  let boundaryHit = false;
  let substringHit = false;
  for (let tierIndex = 0; tierIndex < fields.tiers.length; tierIndex += 1) {
    const quality = qualityInTier(word, fields.tiers[tierIndex]);
    if (quality === 'none') continue;
    if (quality === 'exact' || quality === 'prefix') boundaryHit = true;
    else substringHit = true;
    best = Math.max(best, TIER_WEIGHTS[tierIndex] + qualityBonus(word.length, quality));
  }
  return (word.length <= 2 ? boundaryHit : boundaryHit || substringHit) ? best : null;
}

function scoreExactToken(word: string, fields: NormalizedFields): number | null {
  let best = 0;
  for (let tierIndex = 0; tierIndex < fields.tiers.length; tierIndex += 1) {
    if (qualityInTier(word, fields.tiers[tierIndex]) !== 'exact') continue;
    best = Math.max(
      best,
      TIER_WEIGHTS[tierIndex] + qualityBonus(word.length, 'exact'),
    );
  }
  return best > 0 ? best : null;
}

function matchingTokenIds(
  word: string,
  index: PrayerSearchIndex,
  metrics?: PrayerSearchMetrics,
  exactTokensOnly = false,
): readonly number[] {
  if (exactTokensOnly) {
    if (metrics) metrics.tokenComparisons += 1;
    const tokenId = index.tokenIdByToken.get(word);
    return tokenId === undefined ? [] : [tokenId];
  }
  if (word.length <= 2) return index.shortPrefixTokenIds.get(word) ?? [];
  const grams = uniqueNgrams(word, 3);
  let seed: readonly number[] | undefined;
  for (const gram of grams) {
    const candidates = index.trigramTokenIds.get(gram);
    if (!candidates) return [];
    if (!seed || candidates.length < seed.length) seed = candidates;
  }
  if (!seed) return [];
  const matched: number[] = [];
  for (const tokenId of seed) {
    if (metrics) metrics.tokenComparisons += 1;
    if (index.tokenList[tokenId].includes(word)) matched.push(tokenId);
  }
  return matched;
}

function postingEstimate(tokenIds: readonly number[], index: PrayerSearchIndex): number {
  let estimate = 0;
  for (const tokenId of tokenIds) estimate += index.postings[tokenId].length;
  return estimate;
}

function candidateSet(
  word: string,
  tokenIds: readonly number[],
  index: PrayerSearchIndex,
  current: Set<number> | null,
  metrics?: PrayerSearchMetrics,
  exactTokensOnly = false,
): Set<number> {
  const next = new Set<number>();
  if (current) {
    // Once the rarest group has seeded candidates, testing that small set's
    // cached fields is cheaper than walking every posting for a broad second
    // word (for example "common rare-person").
    for (const prayerIndex of current) {
      if (metrics) metrics.tokenComparisons += 1;
      const score = exactTokensOnly
        ? scoreExactToken(word, index.fields[prayerIndex])
        : scoreWord(word, index.fields[prayerIndex]);
      if (score !== null) next.add(prayerIndex);
    }
    return next;
  }
  for (const tokenId of tokenIds) {
    for (const posting of index.postings[tokenId]) {
      if (metrics) metrics.postingVisits += 1;
      next.add(Math.floor(posting / 32));
    }
  }
  return next;
}

function createdTime(prayer: Prayer): number {
  const value = Date.parse(prayer.createdAt);
  return Number.isFinite(value) ? value : 0;
}

function compareStablePrayerOrder(
  left: { prayer: Prayer; index: number },
  right: { prayer: Prayer; index: number },
): number {
  const timeDifference = createdTime(right.prayer) - createdTime(left.prayer);
  if (timeDifference !== 0) return timeDifference;
  if (left.prayer.id < right.prayer.id) return -1;
  if (left.prayer.id > right.prayer.id) return 1;
  return left.index - right.index;
}

function rankPrayerIndexExactEntries(
  prepared: ReturnType<typeof preparedQuery>,
  index: PrayerSearchIndex,
  metrics?: PrayerSearchMetrics,
  exactTokensOnly = false,
  sortResults = true,
): Array<{ prayer: Prayer; index: number; score: number }> {
  if (prepared.normalized === '') {
    return index.prayers.map((prayer, prayerIndex) => ({
      prayer,
      index: prayerIndex,
      score: 0,
    }));
  }
  if (prepared.words.length === 0) return [];
  if (metrics) metrics.queryTokenCount = prepared.words.length;

  const groups = prepared.words.map((word) => ({
    word,
    tokenIds: matchingTokenIds(word, index, metrics, exactTokensOnly),
  }));
  if (groups.some((group) => group.tokenIds.length === 0)) return [];
  groups.sort(
    (left, right) =>
      postingEstimate(left.tokenIds, index) - postingEstimate(right.tokenIds, index),
  );

  let candidates: Set<number> | null = null;
  for (const group of groups) {
    candidates = candidateSet(
      group.word,
      group.tokenIds,
      index,
      candidates,
      metrics,
      exactTokensOnly,
    );
    if (candidates.size === 0) return [];
  }
  if (!candidates) return [];
  if (metrics) metrics.candidateCount = candidates.size;

  const scored: Array<{ prayer: Prayer; index: number; score: number }> = [];
  for (const prayerIndex of candidates) {
    if (metrics) metrics.prayerEvaluations += 1;
    let score = 0;
    let included = true;
    for (const word of prepared.words) {
      const wordScore = exactTokensOnly
        ? scoreExactToken(word, index.fields[prayerIndex])
        : scoreWord(word, index.fields[prayerIndex]);
      if (wordScore === null) {
        included = false;
        break;
      }
      score += wordScore;
    }
    if (included) scored.push({ prayer: index.prayers[prayerIndex], index: prayerIndex, score });
  }
  if (sortResults) {
    scored.sort((left, right) =>
      right.score !== left.score
        ? right.score - left.score
        : compareStablePrayerOrder(left, right),
    );
  }
  return scored;
}

function rankPrayerIndexExact(
  prepared: ReturnType<typeof preparedQuery>,
  index: PrayerSearchIndex,
  metrics?: PrayerSearchMetrics,
): Prayer[] {
  return rankPrayerIndexExactEntries(prepared, index, metrics).map(
    (entry) => entry.prayer,
  );
}

function rankPrayerIndexStrictRelatedEntries(
  prepared: ReturnType<typeof preparedQuery>,
  index: PrayerSearchIndex,
  metrics?: PrayerSearchMetrics,
): Array<{ prayer: Prayer; index: number; score: number }> {
  if (prepared.words.length === 0) return [];
  if (metrics) metrics.queryTokenCount = prepared.words.length;

  // Related words are topical retrieval hints, never a reason to reinterpret
  // a person's name (for example career -> somebody named Job). Exact-token
  // postings already encode each field tier, so intersect only the semantic
  // summary/raw/update/testimony bits and avoid rescanning cached field text.
  const perWord = prepared.words.map((word) => {
    if (metrics) metrics.tokenComparisons += 1;
    const matches = new Map<number, number>();
    const tokenId = index.tokenIdByToken.get(word);
    if (tokenId === undefined) return matches;
    for (const posting of index.postings[tokenId]) {
      if (metrics) metrics.postingVisits += 1;
      const semanticMask = posting & 30;
      if (semanticMask === 0) continue;
      matches.set(
        Math.floor(posting / 32),
        bestWeight(semanticMask) + qualityBonus(word.length, 'exact'),
      );
    }
    return matches;
  });
  if (perWord.some((matches) => matches.size === 0)) return [];
  perWord.sort((left, right) => left.size - right.size);

  const scored: Array<{ prayer: Prayer; index: number; score: number }> = [];
  for (const [prayerIndex, seedScore] of perWord[0]) {
    let score = seedScore;
    let included = true;
    for (let wordIndex = 1; wordIndex < perWord.length; wordIndex += 1) {
      const wordScore = perWord[wordIndex].get(prayerIndex);
      if (wordScore === undefined) {
        included = false;
        break;
      }
      score += wordScore;
    }
    if (!included) continue;
    if (metrics) metrics.prayerEvaluations += 1;
    scored.push({ prayer: index.prayers[prayerIndex], index: prayerIndex, score });
  }
  if (metrics) metrics.candidateCount = scored.length;
  return scored;
}

function fuzzyThreshold(length: number): number {
  return length >= FUZZY_LONG_LEN ? 2 : 1;
}

interface FuzzyBudget {
  tokenVisits: number;
  editCalls: number;
}

interface FuzzyTokenMatch {
  tokenId: number;
  distance: number;
  prefix: number;
}

function fuzzyTokenMatches(
  word: string,
  index: PrayerSearchIndex,
  budget: FuzzyBudget,
  metrics?: PrayerSearchMetrics,
): FuzzyTokenMatch[] {
  if (
    word.length < FUZZY_MIN_LEN ||
    word.length > PRAYER_SEARCH_MAX_FUZZY_TOKEN_LENGTH ||
    budget.editCalls >= MAX_FUZZY_EDIT_CALLS
  ) return [];
  const threshold = fuzzyThreshold(word.length);
  const sources: Array<{ ids: readonly number[]; weight: number }> = [];
  for (const gram of uniqueNgrams(word, 2)) {
    const ids = index.bigramTokenIds.get(gram);
    if (ids) sources.push({ ids, weight: 2 });
  }
  for (const character of new Set(Array.from(word))) {
    const ids = index.charTokenIds.get(character);
    if (ids) sources.push({ ids, weight: 1 });
  }
  sources.sort((left, right) => left.ids.length - right.ids.length);
  const candidateStrength = new Map<number, number>();
  for (const source of sources) {
    for (const tokenId of source.ids) {
      if (budget.tokenVisits >= MAX_FUZZY_TOKEN_VISITS) break;
      budget.tokenVisits += 1;
      if (metrics) metrics.fuzzyTokenVisits += 1;
      const token = index.tokenList[tokenId];
      if (
        token === word ||
        Math.abs(token.length - word.length) > threshold ||
        token.length > PRAYER_SEARCH_MAX_FUZZY_TOKEN_LENGTH
      ) continue;
      candidateStrength.set(tokenId, (candidateStrength.get(tokenId) ?? 0) + source.weight);
    }
    if (budget.tokenVisits >= MAX_FUZZY_TOKEN_VISITS) break;
  }

  const ranked = [...candidateStrength].sort((left, right) => {
    if (right[1] !== left[1]) return right[1] - left[1];
    const leftToken = index.tokenList[left[0]];
    const rightToken = index.tokenList[right[0]];
    const prefixDifference =
      commonPrefixLength(word, rightToken) - commonPrefixLength(word, leftToken);
    if (prefixDifference !== 0) return prefixDifference;
    const lengthDifference =
      Math.abs(leftToken.length - word.length) - Math.abs(rightToken.length - word.length);
    if (lengthDifference !== 0) return lengthDifference;
    return leftToken < rightToken ? -1 : leftToken > rightToken ? 1 : 0;
  });

  const matches: FuzzyTokenMatch[] = [];
  for (const [tokenId] of ranked) {
    if (budget.editCalls >= MAX_FUZZY_EDIT_CALLS) break;
    const token = index.tokenList[tokenId];
    budget.editCalls += 1;
    if (metrics) metrics.editDistanceCalls += 1;
    const distance = osaDistanceWithin(word, token, threshold);
    if (distance <= threshold) {
      matches.push({
        tokenId,
        distance,
        prefix: commonPrefixLength(word, token),
      });
    }
  }
  return matches;
}

function bestWeight(tierMask: number): number {
  for (let index = 0; index < TIER_WEIGHTS.length; index += 1) {
    if ((tierMask & (1 << index)) !== 0) return TIER_WEIGHTS[index];
  }
  return 0;
}

interface FuzzyPrayerWordMatch {
  exact: boolean;
  distance: number;
  weight: number;
  prefix: number;
}

function fuzzyPrayerMatchesForWord(
  word: string,
  index: PrayerSearchIndex,
  budget: FuzzyBudget,
  metrics?: PrayerSearchMetrics,
): Map<number, FuzzyPrayerWordMatch> {
  const matches = new Map<number, FuzzyPrayerWordMatch>();
  const exactTokenId = index.tokenIdByToken.get(word);
  if (exactTokenId !== undefined) {
    for (const posting of index.postings[exactTokenId]) {
      if (metrics) metrics.postingVisits += 1;
      const prayerIndex = Math.floor(posting / 32);
      matches.set(prayerIndex, {
        exact: true,
        distance: 0,
        weight: bestWeight(posting & 31),
        prefix: word.length,
      });
    }
  }
  for (const tokenMatch of fuzzyTokenMatches(word, index, budget, metrics)) {
    for (const posting of index.postings[tokenMatch.tokenId]) {
      if (metrics) metrics.postingVisits += 1;
      const prayerIndex = Math.floor(posting / 32);
      const candidate: FuzzyPrayerWordMatch = {
        exact: false,
        distance: tokenMatch.distance,
        weight: bestWeight(posting & 31),
        prefix: tokenMatch.prefix,
      };
      const current = matches.get(prayerIndex);
      if (
        !current ||
        (!current.exact && (
          candidate.distance < current.distance ||
          (candidate.distance === current.distance && candidate.prefix > current.prefix) ||
          (candidate.distance === current.distance &&
            candidate.prefix === current.prefix && candidate.weight > current.weight)
        ))
      ) matches.set(prayerIndex, candidate);
    }
  }
  return matches;
}

function rankPrayerIndexFuzzy(
  prepared: ReturnType<typeof preparedQuery>,
  index: PrayerSearchIndex,
  metrics?: PrayerSearchMetrics,
): Prayer[] {
  if (
    prepared.normalized === '' ||
    prepared.words.length === 0 ||
    prepared.words.length > PRAYER_SEARCH_MAX_FUZZY_TOKENS
  ) return [];
  if (metrics) metrics.queryTokenCount = prepared.words.length;
  const budget: FuzzyBudget = { tokenVisits: 0, editCalls: 0 };
  const perWord = prepared.words.map((word) =>
    fuzzyPrayerMatchesForWord(word, index, budget, metrics),
  );
  if (perWord.some((matches) => matches.size === 0)) return [];
  perWord.sort((left, right) => left.size - right.size);
  const candidates = [...perWord[0].keys()].filter((prayerIndex) =>
    perWord.every((matches) => matches.has(prayerIndex)),
  );
  if (metrics) metrics.candidateCount = candidates.length;
  const multiWord = prepared.words.length > 1;
  const scored: Array<{
    prayer: Prayer;
    index: number;
    distance: number;
    weight: number;
    prefix: number;
  }> = [];
  for (const prayerIndex of candidates) {
    if (metrics) metrics.prayerEvaluations += 1;
    const wordMatches = perWord.map((matches) => matches.get(prayerIndex)!);
    if (multiWord && !wordMatches.some((match) => match.exact)) continue;
    scored.push({
      prayer: index.prayers[prayerIndex],
      index: prayerIndex,
      distance: wordMatches.reduce((total, match) => total + match.distance, 0),
      weight: wordMatches.reduce((total, match) => total + match.weight, 0),
      prefix: wordMatches.reduce((total, match) => total + match.prefix, 0),
    });
  }
  scored.sort((left, right) =>
    left.distance !== right.distance
      ? left.distance - right.distance
      : right.prefix !== left.prefix
        ? right.prefix - left.prefix
        : right.weight !== left.weight
          ? right.weight - left.weight
          : compareStablePrayerOrder(left, right),
  );
  return scored.map((entry) => entry.prayer);
}

export function searchPrayerIndex(
  query: string,
  index: PrayerSearchIndex,
  metrics?: PrayerSearchMetrics,
  relatedTerms: readonly string[] = [],
): PrayerSearchResult {
  const prepared = preparedQuery(query);
  const exactEntries = rankPrayerIndexExactEntries(prepared, index, metrics);
  const exact = exactEntries.map((entry) => entry.prayer);
  if (prepared.normalized === '') {
    return {
      prayers: exact,
      fuzzy: false,
      related: false,
      matchKindByPrayerId: new Map(
        exact.map((prayer) => [prayer.id, 'direct' as const]),
      ),
      normalizedQuery: prepared.normalized,
      queryTokens: prepared.words,
    };
  }

  // A user often remembers the concern rather than the exact wording they
  // used ("anxiety" vs "worried"). Curated related wording augments literal
  // recall on every eligible query, while literal matches always occupy the
  // first result band. Related candidates are de-duped against that band and
  // remain individually identifiable to the UI. Expansion is bounded before
  // any index work. Multiple related terms that reach the same prayer
  // strengthen it; stable prayer order remains the final tiebreak.
  const normalizedRelated = [...new Set(
    relatedTerms
      .slice(0, PRAYER_SEARCH_MAX_RELATED_TERMS)
      .map((term) => preparedQuery(term))
      .filter(
        (term) =>
          term.normalized !== '' &&
          term.normalized !== prepared.normalized &&
          term.words.length > 0,
      )
      .map((term) => term.normalized),
  )];
  let relatedPrayers: Prayer[] = [];
  if (normalizedRelated.length > 0) {
    const relatedMatches = new Map<number, { hits: number; bestScore: number }>();
    for (const term of normalizedRelated) {
      const ranked = rankPrayerIndexStrictRelatedEntries(
        preparedQuery(term),
        index,
        metrics,
      );
      for (const entry of ranked) {
        const prayerIndex = entry.index;
        const current = relatedMatches.get(prayerIndex);
        if (current) {
          current.hits += 1;
          current.bestScore = Math.max(current.bestScore, entry.score);
        } else {
          relatedMatches.set(prayerIndex, { hits: 1, bestScore: entry.score });
        }
      }
    }
    if (relatedMatches.size > 0) {
      const exactPrayerIndexes = new Set(exactEntries.map((entry) => entry.index));
      const related = [...relatedMatches].map(([prayerIndex, match]) => ({
        prayer: index.prayers[prayerIndex],
        index: prayerIndex,
        ...match,
      })).filter((entry) => !exactPrayerIndexes.has(entry.index));
      related.sort((left, right) =>
        right.hits !== left.hits
          ? right.hits - left.hits
          : right.bestScore !== left.bestScore
            ? right.bestScore - left.bestScore
            : compareStablePrayerOrder(left, right),
      );
      relatedPrayers = related.map(({ prayer }) => prayer);
    }
  }

  if (exact.length > 0 || relatedPrayers.length > 0) {
    const matchKindByPrayerId = new Map<string, PrayerSearchMatchKind>();
    for (const prayer of exact) matchKindByPrayerId.set(prayer.id, 'direct');
    for (const prayer of relatedPrayers) matchKindByPrayerId.set(prayer.id, 'related');
    return {
      prayers: [...exact, ...relatedPrayers],
      fuzzy: false,
      related: relatedPrayers.length > 0,
      matchKindByPrayerId,
      normalizedQuery: prepared.normalized,
      queryTokens: prepared.words,
    };
  }

  const fuzzy = rankPrayerIndexFuzzy(prepared, index, metrics);
  return {
    prayers: fuzzy,
    fuzzy: fuzzy.length > 0,
    related: false,
    matchKindByPrayerId: new Map(
      fuzzy.map((prayer) => [prayer.id, 'fuzzy' as const]),
    ),
    normalizedQuery: prepared.normalized,
    queryTokens: prepared.words,
  };
}

export function prayerSearchContextKind(
  prayer: Prayer,
  query: string,
  visibleSummary = prayer.gardenSummary ?? '',
): PrayerSearchContextKind {
  const prepared = preparedQuery(query);
  if (prepared.words.length === 0) return 'details';
  // Called only for mounted result cards, so derive these two presentation
  // contexts on demand rather than retaining a second copy of every large raw
  // prayer/update/testimony string in the search index.
  const visibleText = normalizeForSearch(
    [prayer.parsed?.who, visibleSummary]
      .filter((part): part is string => !!part)
      .join(' '),
  );
  const authoredText = normalizeForSearch(
    [
      prayer.rawText,
      ...(prayer.progressHistory ?? []).map((entry) => entry.note),
      prayer.testimony,
    ]
      .filter((part): part is string => !!part)
      .join(' '),
  );
  if (prepared.words.every((word) => visibleText.includes(word))) return 'visible';
  if (prepared.words.some((word) => authoredText.includes(word))) return 'authored';
  return 'details';
}

const compatibilityIndexCache = new WeakMap<Prayer[], PrayerSearchIndex>();

function compatibilityIndex(prayers: Prayer[]): PrayerSearchIndex {
  const cached = compatibilityIndexCache.get(prayers);
  if (cached) return cached;
  const built = buildPrayerSearchIndex(prayers, 'compatibility');
  compatibilityIndexCache.set(prayers, built);
  return built;
}

/** Boolean exact matcher in input order; empty query preserves array identity. */
export function searchPrayers(query: string, prayers: Prayer[]): Prayer[] {
  const prepared = preparedQuery(query);
  if (prepared.normalized === '') return prayers;
  const matched = new Set(rankPrayerIndexExact(prepared, compatibilityIndex(prayers)));
  return prayers.filter((prayer) => matched.has(prayer));
}

/** Relevance-ranked exact matcher with deterministic id fallback. */
export function rankPrayers(query: string, prayers: Prayer[]): Prayer[] {
  const prepared = preparedQuery(query);
  if (prepared.normalized === '') return prayers;
  return rankPrayerIndexExact(prepared, compatibilityIndex(prayers));
}

/** Bounded typo fallback. Call only after the exact matcher returns no rows. */
export function rankPrayersFuzzy(query: string, prayers: Prayer[]): Prayer[] {
  return rankPrayerIndexFuzzy(preparedQuery(query), compatibilityIndex(prayers));
}

export function findRelatedPrayers(
  newText: string,
  existing: Prayer[],
  limit = 4
): Prayer[] {
  const queryTokens = tokenize(newText);
  if (queryTokens.length === 0) return [];

  const scored = existing.map((p) => {
    const rawScore = overlapScore(queryTokens, tokenize(p.rawText));
    const parsedBonus = p.parsed
      ? overlapScore(queryTokens, tokenize(p.parsed.who + ' ' + p.parsed.core_prayer)) * 2
      : 0;
    // Updates carry the user's real remembered language ("chemo started
    // Monday"); score them at 1× so recent context bubbles up without
    // dominating the parsed-field bonus.
    const historyBonus = p.progressHistory?.length
      ? overlapScore(queryTokens, tokenize(p.progressHistory.map((e) => e.note).join(' ')))
      : 0;
    return { prayer: p, score: rawScore + parsedBonus + historyBonus };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.prayer);
}

export function buildRelatedContext(related: Prayer[]): string {
  if (related.length === 0) return '';
  const lines = related.map((p, i) => {
    const who = p.parsed?.who ?? 'General';
    const snippet = p.rawText.length > 120 ? p.rawText.slice(0, 117).trimEnd() + '...' : p.rawText;
    return `${i + 1}. "${snippet}" (prayed for: ${who})`;
  });
  return (
    'For context, here are related prayers the user has prayed before:\n' +
    lines.join('\n') +
    '\n\nNow process this new prayer:'
  );
}
